import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, RadioGroup, FormControlLabel, Radio,
  TextField, Typography, Box
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { reportarChat } from '../../actions/chat';

// Las claves son las que acepta el servidor (routes/api/chat.js); el texto es lo que ve la persona
export const MOTIVOS_REPORTE = [
  { clave: 'spam', texto: 'Spam o publicidad' },
  { clave: 'acoso', texto: 'Acoso o groserías' },
  { clave: 'estafa', texto: 'Posible estafa o fraude' },
  { clave: 'inapropiado', texto: 'Contenido inapropiado' },
  { clave: 'otro', texto: 'Otro motivo' },
];
export const DETALLE_MAX = 500;   // igual que el servidor (server/config/limitesChat.js)

// Reportar una conversación: se elige el motivo y, si se quiere, se explica. El equipo recibe un ticket con los últimos mensajes.
const ReportarChatDialog = ({ open, onClose, chatId, nombre, zIndex }) => {
  const dispatch = useDispatch();
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState({});
  const [resultado, setResultado] = useState(null);   // { numero, repetido } cuando ya se envió

  useEffect(() => {
    if (open) { setMotivo(''); setDetalle(''); setEnviando(false); setErrores({}); setResultado(null); }
  }, [open]);

  const enviar = async () => {
    if (!motivo) { setErrores({ motivo: 'Elige el motivo del reporte' }); return; }
    setErrores({});
    setEnviando(true);
    const r = await dispatch(reportarChat(chatId, { motivo, detalle: detalle.trim() }));
    setEnviando(false);
    if (r.ok) setResultado({ numero: r.numero, repetido: r.repetido });
    else setErrores({ envio: r.error });
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="xs" fullWidth sx={zIndex ? { zIndex } : undefined}>
      {resultado ? (
        <>
          <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {resultado.repetido ? 'Ya tenemos tu reporte' : 'Reporte enviado'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {resultado.repetido
                ? `Ya hay un reporte tuyo abierto sobre esta persona (${resultado.numero}). El equipo lo está revisando.`
                : `Gracias. El equipo revisará la conversación y te avisaremos si necesitamos algo más. Tu folio es ${resultado.numero}.`}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', borderRadius: 2, px: 4 }}>Cerrar</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>Reportar a {nombre || 'esta persona'}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
              ¿Qué pasó? Se enviarán al equipo los últimos mensajes de esta conversación para que puedan revisarla.
            </Typography>
            <RadioGroup value={motivo} onChange={(e) => { setMotivo(e.target.value); setErrores({}); }}>
              {MOTIVOS_REPORTE.map((m) => (
                <FormControlLabel key={m.clave} value={m.clave} control={<Radio size="small" />} label={m.texto} />
              ))}
            </RadioGroup>
            {errores.motivo && <Typography role="alert" variant="caption" sx={{ color: 'error.oscuro' }}>{errores.motivo}</Typography>}
            <TextField
              fullWidth multiline minRows={2} size="small" sx={{ mt: 2 }}
              label="Cuéntanos más (opcional)"
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              inputProps={{ maxLength: DETALLE_MAX }}
              helperText={`${detalle.length}/${DETALLE_MAX}`}
            />
            {errores.envio && (
              <Box role="alert" sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'error.oscuro' }}>{errores.envio}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} disabled={enviando} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancelar</Button>
            <Button variant="contained" onClick={enviar} disabled={enviando} sx={{ textTransform: 'none', borderRadius: 2 }}>
              {enviando ? 'Enviando…' : 'Enviar reporte'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

ReportarChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chatId: PropTypes.string,
  nombre: PropTypes.string,
  zIndex: PropTypes.number,
};

export default ReportarChatDialog;
